import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { updateSEO } from '../../../utils/seo';

// ===== 専門資格ページ 新デザイン全面最新化（型正典: src/pages/home/page.tsx / src/pages/about/overview/page.tsx） =====
// 設計note（共通骨格・9ページ共通の設計言語）:
//   紺#1a2942 / 真鍮金#a38e65 / brass2 #8f7a56 / gold-signboard #d7b779 / cream #f8f6f0 / line #e2ddd2。
//   見出し=jba-display（Shippori Mincho B1）+ 金差し色span。eyebrow=Playfair uppercase。BTN-08 2状態。
//   共通セクション骨格（全異型・装飾ディバイダー/素hr/❦/紋章 不使用）:
//     Hero(全幅ビルボード) → I.資格概要(リード3行+細罫データ行+特徴3枚) → II.カリキュラム(番号2カラム編集ブロック)
//     → III.こんな方におすすめ(紺パネル+2カラム点検リスト) → IV.教材(番号カード) → V.認定試験(紺罫定義リスト)
//     → VI.料金とお申込み(大字serif受講料+含まれるもの) → VII.FAQ(アコーディオン) → VIII.関連資格+締めCTA。
//   個性の付け方（機械複製にしない・区画統一は保つ）: ①hero型(A=左寄せ全幅/B=中央額装/C=下部帯)を資格群で振り分け
//     ②差し色の主役セクション(紺パネルをIII or VIに置く)を資格で変える ③画像3枚の配置点を資格内容に合わせる。
//   実文出典（創作禁止）: 現行working tree実文（本ページ旧実装の逐語）+ mng-thebutler/00_事業説明.md（協会理念/沿革）。
//     数値・受講料・章立て・FAQは旧実装から改変せず継承。
// business-manner = hero型A(左寄せ全幅) / 紺パネル主役=III.

export default function BusinessMannerButlerPage() {
  useEffect(() => {
    updateSEO({
      title: '執事資格 Business Manner | 執事検定専門認定 - 全日本執事協会',
      description: 'Business Manner執事資格は、ビジネスシーンでの適切な振る舞いと対応を指導できる専門家を認定する執事検定です。国際マナー、接遇、企業研修指導など、ビジネス分野で活躍できる執事資格です。',
      keywords: 'Business Manner,執事資格,執事検定,ビジネスマナー,専門資格,全日本執事協会',
      canonical: 'https://butler-association.jp/qualifications/business-manner'
    });
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '資格制度', href: '/qualifications' },
    { label: 'Business Manner' }
  ];

  // BTN-08（canon と同一文法・2状態）
  const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const features = [
    { icon: 'ri-global-line', title: '国際マナー', desc: 'グローバルに通用するビジネス作法。各国のプロトコルと接遇を、執事の品格ある所作へ落とし込みます。' },
    { icon: 'ri-earth-line', title: '異文化理解', desc: '各国の習慣・タブーへの配慮。相手の文化的背景を読み、失礼のない振る舞いを体系立てて学びます。' },
    { icon: 'ri-presentation-line', title: '実践指導力', desc: '他者に教えられる体系的知識。暗記でなく背景の意味を理解し、状況に応じて実践・指導できる力を養います。' },
  ];

  const curriculum = [
    { no: '01', title: '執事が教えるビジネスマナー', desc: 'なぜ執事がビジネスマナーを教えるのか、ホスピタリティとビジネスの関係、品格ある振る舞いの本質を理解します。' },
    { no: '02', title: 'ビジネスシーンの基本マナー', desc: '名刺交換の作法、会議室でのマナー、エレベーター・車の席次、訪問時・来客対応の基本を習得します。' },
    { no: '03', title: 'ビジネスコミュニケーション', desc: '電話応対の実践技術、メール・文書の書き方、報告・連絡・相談の徹底、適切な言葉遣いと敬語の使い方を学びます。' },
    { no: '04', title: '会議・商談のマナー', desc: '会議の準備と設営方法、議事進行のサポート技術、飲料・茶菓の適切な提供、会議後のフォローアップを習得します。' },
    { no: '05', title: '接待・会食のマナー', desc: 'レストランの予約と選定基準、席次とエスコートの実践、会話のファシリテート技術、会計のスマートな処理方法を学びます。' },
    { no: '06', title: '国際ビジネスマナー', desc: '各国のビジネス習慣と特徴、異文化理解と適切な配慮、宗教的タブーへの注意、非言語コミュニケーションの理解を習得します。' },
    { no: '07', title: 'プロトコル（国際儀礼）', desc: 'プロトコルの基本概念、公式行事での作法、国旗・国歌の正しい扱い方、席次と序列の原則を学びます。' },
    { no: '08', title: 'VIPビジネス対応', desc: '役員秘書としての執事の役割、重要顧客への高度な対応、プライバシーと機密保持の徹底、先回りした準備の技術を習得します。' },
    { no: '09', title: 'ビジネスイベントの運営', desc: 'セミナー・講演会の設営支援、レセプション・パーティーの運営、展示会・商談会のサポート、社内イベントの企画方法を学びます。' },
    { no: '10', title: '総合演習', desc: 'ビジネスシーン別のケーススタディ、実践シミュレーション、確認テストで学習内容を統合します。' },
  ];

  const recommend = [
    'ビジネスマナー講師を目指す方', '企業の人事・研修担当者', '役員秘書・エグゼクティブアシスタント',
    '接客業でスキルアップしたい方', '国際ビジネスに携わる方', 'コンサルタント・士業の方',
    'キャリアアップを目指すビジネスパーソン', '執事資格と組み合わせて専門性を高めたい方',
  ];

  const materials = [
    { no: '1', title: 'テキスト教材（約100ページ）', desc: 'ビジネスマナーと国際儀礼の専門知識を詳しく解説。' },
    { no: '2', title: 'オンライン動画レッスン', desc: '実際のビジネスシーンを想定した実践的な動画解説。視聴期間1年間。' },
    { no: '3', title: '国際プロトコル早見表', desc: '各国のビジネス習慣とタブーをまとめた便利な参考資料。' },
    { no: '4', title: '認定試験問題・解答用紙', desc: '筆記試験とシチュエーション別対応課題を含む試験問題。' },
  ];

  const exam = [
    { label: '試験形式', value: '筆記試験（記述式・選択式・ケーススタディ）' },
    { label: '合格基準', value: '80点以上' },
    { label: '試験期限', value: '申込から1年以内' },
    { label: '受験資格', value: '特になし（Associate Butler以上推奨）' },
    { label: '受験場所', value: '自宅（通信試験）' },
    { label: '再試験', value: '再試験料10,000円で何度でも可' },
  ];

  const included = [
    'テキスト教材（約100ページ）', 'オンライン動画レッスン（1年間視聴可能）', '国際プロトコル早見表',
    '認定試験問題・解答用紙', '試験添削サービス', '合格時の認定証・認定カード', '送料',
  ];

  const related = [
    { href: '/qualifications/travel-concierge', name: 'Travel Concierge', ja: '旅行サポートの専門家', icon: 'ri-plane-line' },
    { href: '/qualifications/event-planning', name: 'Event Planning', ja: 'イベント企画の専門家', icon: 'ri-calendar-event-line' },
    { href: '/qualifications', name: '専門資格一覧を見る', ja: '全ての専門資格をチェック', icon: 'ri-list-check' },
  ];

  const faqs = [
    { q: 'ビジネス経験がなくても取得できますか？', a: 'はい、基礎から学べる内容です。学生の方や異業種からの転職を考える方にも適しています。' },
    { q: 'マナー講師として活動できますか？', a: '本資格は就職を保証するものではありませんが、マナー講師を目指す方の知識習得に役立つ内容です。' },
    { q: '企業研修に活用できますか？', a: 'はい、企業の人事・研修担当者が社内教育の参考として活用できる体系的な知識が学べます。' },
    { q: '国際的な仕事に役立ちますか？', a: 'はい、第6章と第7章で国際ビジネスマナーとプロトコルを詳しく学びます。' },
    { q: '秘書検定などの他の資格との違いは？', a: '執事の視点から品格とホスピタリティを重視した独自のアプローチが特徴です。' },
    { q: 'オンライン会議のマナーも学べますか？', a: '基本的なビジネスコミュニケーションの原則を学ぶため、オンラインでも応用可能です。' },
    { q: '接客業で働いていますが役立ちますか？', a: 'はい、VIP対応や高度な接遇技術の習得に直結する内容です。' },
    { q: '名刺交換の細かい作法も学べますか？', a: 'はい、第2章で名刺交換の作法を詳しく学びます。' },
    { q: '異文化理解とは具体的にどんな内容ですか？', a: '各国のビジネス習慣、宗教的タブー、コミュニケーションスタイルの違いなどを学びます。' },
    { q: 'プロトコルとビジネスマナーの違いは？', a: 'プロトコルは国際儀礼、ビジネスマナーは企業内外での作法です。両方を体系的に学びます。' },
    { q: '会食のマナーも詳しく学べますか？', a: 'はい、第5章でレストラン選びから会計まで実践的に学びます。' },
    { q: '言葉遣いや敬語も学べますか？', a: 'はい、第3章でビジネスに適した言葉遣いと敬語の使い方を学びます。' },
  ];

  return (
    <Layout>
      {/* ===== Hero — 全幅ビルボード型A（画像+紺グラデ+左寄せ）。由来: about/overview ヒーロー ===== */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[540px] lg:min-h-[620px] flex items-center">
        <img
          src="/images/generated/jba-p-019-qual-manner.jpg"
          alt="ビジネスマナー研修風景。執事が企業の場面で接遇作法を指導する様子"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.93)_0%,rgba(16,20,26,0.74)_46%,rgba(16,20,26,0.34)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[680px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.3em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Specialist Certification</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(30px,4vw,50px)] leading-[1.3] tracking-[0.04em] mb-3">Business Manner</h1>
            <p className="font-['Noto_Serif_JP',serif] text-[#d7b779] text-base sm:text-lg tracking-[0.12em] mb-6">ビジネス・マナー</p>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95] max-w-[600px]">
              全日本執事協会が認定する、ビジネス分野専門の執事資格です。名刺交換や会議運営といった基本作法から、国際プロトコル・異文化理解・VIP対応まで、企業の現場で信頼される振る舞いを体系的に習得します。
            </p>
          </div>
        </div>
      </section>

      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8 lg:px-10 py-14 sm:py-20 space-y-16 sm:space-y-24">

          {/* ===== I. 資格概要（リード3行+細罫データ行+特徴3枚）。由来: about/overview S1 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">I. About This Certification</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              執事の品格を、<span className="text-[#a38e65]">ビジネスの現場</span>へ
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
              <p>
                Business Manner資格は、執事が培ってきた品格ある振る舞いとホスピタリティを、ビジネスシーンへ応用する専門知識を学ぶ執事検定です。単なるマナーの暗記ではなく、その背景にある意味や心構えを理解し、状況に応じて適切に実践・指導できる力を養います。
              </p>
              <p>
                名刺交換、電話応対、会議運営、接待マナーといった基本から、国際的なプロトコル（儀礼）、異文化理解、VIP対応まで、グローバルなビジネス環境で求められる幅広い知識を体系的に習得します。企業向けサービスに直結する、実践的な専門資格です。
              </p>
              <p>
                企業研修講師、役員秘書、接客業のコンサルティングなど、ビジネス分野での幅広い活躍が期待できます。執事検定のなかでも、キャリアに直結する専門性の高い資格として評価されています。
              </p>
            </div>

            {/* 細罫データ行 */}
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mt-10 border-t border-[#e2ddd2] pt-8">
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">受講料（税込）</dt>
                <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">58,000<span className="text-sm text-[#8f7a56] ml-1">円</span></dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">学習期間目安</dt>
                <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">2–4<span className="text-sm text-[#8f7a56] ml-1">ヶ月</span></dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">カリキュラム</dt>
                <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">全10<span className="text-sm text-[#8f7a56] ml-1">章</span></dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">受験・資格</dt>
                <dd className="font-['Noto_Serif_JP',serif] text-lg text-[#1a2942] leading-tight pt-1">自宅受験・永久</dd>
              </div>
            </dl>

            {/* 特徴3枚（アイコン編集カード） */}
            <div className="grid sm:grid-cols-3 gap-5 sm:gap-6 mt-12">
              {features.map((f) => (
                <div key={f.title} className="border-t-2 border-[#a38e65] bg-[#f8f6f0] px-6 py-7">
                  <i className={`${f.icon} text-3xl text-[#a38e65]`}></i>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mt-4 mb-2.5">{f.title}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{f.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex items-start gap-3 border border-[#a38e65]/40 bg-[#f8f6f0] px-5 py-4">
              <i className="ri-information-line text-[#a38e65] text-xl mt-0.5"></i>
              <p className="font-['Noto_Serif_JP',serif] text-sm text-[#6b6353] leading-[1.85]">
                本資格は知識習得を目的とした認定制度であり、就職や収入を保証するものではありません。全日本執事協会が認定する民間資格です。
              </p>
            </div>
          </section>

          {/* ===== II. カリキュラム（番号2カラム編集ブロック+画像）。由来: about/overview S4 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">II. Curriculum</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              学ぶ内容 — <span className="text-[#a38e65]">全10章</span>
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-8">
              ビジネスマナーと国際儀礼の専門知識を、10の分野で体系的に学びます。各章は実践的な内容で構成され、知識だけでなく実際の指導技術も習得できます。
            </p>
            <img
              src="/images/generated/jba-fs-152-qualifications_business-manner-v5.jpg"
              alt="ビジネスマナー学習カリキュラムの教材。名刺交換や国際儀礼を解説するテキストと資料"
              className="w-full aspect-[21/9] h-auto object-cover object-center border border-[#a38e65]/30 mb-10"
            />
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
              {curriculum.map((c) => (
                <div key={c.no} className="flex gap-5 border-t border-[#e2ddd2] pt-6">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none shrink-0 pt-1">{c.no}</span>
                  <div>
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2 leading-[1.5]">{c.title}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ===== III. こんな方におすすめ（紺パネル+2カラム点検リスト）= 新バリエーション①
               由来注記: about/overview S3 の紺パネル引用型を、点検リスト（金チェック）主役の紺パネルへ翻案。 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">III. Who It's For</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              こんな方におすすめです
            </h2>
            <div className="bg-[#1a2942] px-6 sm:px-10 lg:px-14 py-10 sm:py-12">
              <div className="grid sm:grid-cols-2 gap-x-10 gap-y-4">
                {recommend.map((r) => (
                  <div key={r} className="flex items-start gap-3 border-b border-white/10 pb-4">
                    <i className="ri-check-line text-[#d7b779] text-xl mt-0.5 shrink-0"></i>
                    <span className="font-['Noto_Serif_JP',serif] text-[15px] text-[#ded8cc] leading-[1.8]">{r}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ===== IV. お届けする教材（番号カード+画像）。由来: about/overview S2 の罫線カード文法 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">IV. Course Materials</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              お届けする教材
            </h2>
            <div className="grid lg:grid-cols-[1fr_360px] gap-8 lg:gap-12 items-start">
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                {materials.map((m) => (
                  <div key={m.no} className="flex gap-4 border-t border-[#e2ddd2] pt-5">
                    <span className="font-['Playfair_Display',serif] text-[#a38e65] text-xl w-8 h-8 shrink-0 flex items-center justify-center border border-[#a38e65]/40 rounded-full">{m.no}</span>
                    <div>
                      <h3 className="jba-display font-medium text-[#1a2942] text-base mb-1.5 leading-[1.5]">{m.title}</h3>
                      <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85]">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <img
                src="/images/generated/jba-fs-153-qualifications_business-manner-v5.jpg"
                alt="ビジネスマナー教材一式。テキスト・動画レッスン・国際プロトコル早見表がそろった学習セット"
                className="w-full h-56 lg:h-[300px] object-cover border border-[#a38e65]/30"
              />
            </div>
          </section>

          {/* ===== V. 認定試験について（紺罫定義リスト）= 新バリエーション②
               由来注記: about/overview S2 の border-t-2 定義リスト文法を、試験要項の罫線テーブルへ適用。 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">V. Examination</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              認定試験について
            </h2>
            <dl className="border-t-2 border-[#1a2942] max-w-3xl">
              {exam.map((row) => (
                <div key={row.label} className="flex flex-col sm:flex-row gap-1 sm:gap-6 border-b border-[#e2ddd2] py-4">
                  <dt className="sm:w-[140px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] pt-0.5">{row.label}</dt>
                  <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.85]">{row.value}</dd>
                </div>
              ))}
            </dl>
            <p className="font-['Noto_Serif_JP',serif] text-sm text-[#6b6353] leading-[1.9] mt-5 max-w-3xl">
              試験は自宅で行う通信試験です。教材が届いたら、ご自身のペースで学習を進め、準備ができた段階で試験問題に取り組みます。解答用紙を返送いただくと、当協会が添削・採点し、80点以上で合格。認定証と認定カードを発行します。
            </p>
          </section>

          {/* ===== VI. 料金とお申込み（大字serif受講料+含まれるもの）。受講料は22–26px級以上serif強調 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">VI. Enrollment</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              料金とお申込み
            </h2>
            <div className="bg-[#f8f6f0] border border-[#a38e65]/30 px-6 sm:px-10 lg:px-12 py-10">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 border-b border-[#e2ddd2] pb-8 mb-8">
                <div>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.08em] mb-2">受講料（税込・送料込）</p>
                  <div className="jba-display text-[#1a2942] leading-none">
                    <span className="font-['Playfair_Display',serif] text-[clamp(44px,7vw,64px)] font-semibold">58,000</span>
                    <span className="text-2xl sm:text-3xl ml-1">円</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-6 text-center sm:text-right">
                  <div>
                    <p className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] mb-1">学習期間目安</p>
                    <p className="jba-display text-[#1a2942] text-base">2〜4ヶ月</p>
                  </div>
                  <div>
                    <p className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] mb-1">試験期限</p>
                    <p className="jba-display text-[#1a2942] text-base">1年以内</p>
                  </div>
                  <div>
                    <p className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] mb-1">資格有効期限</p>
                    <p className="jba-display text-[#1a2942] text-base">永久</p>
                  </div>
                </div>
              </div>
              <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-5">料金に含まれるもの</h3>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {included.map((it) => (
                  <div key={it} className="flex items-start gap-3">
                    <i className="ri-check-line text-[#a38e65] text-lg mt-0.5 shrink-0"></i>
                    <span className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.8]">{it}</span>
                  </div>
                ))}
              </div>
              <div className="mt-9">
                <a href="/contact" className={`${BTN08_ON_LIGHT} w-full sm:w-auto`}>Business Manner資格に申し込む</a>
              </div>
            </div>
            <ul className="mt-6 space-y-2">
              {['本資格は全日本執事協会が認定する民間資格です', '資格取得により就職・転職・収入を保証するものではありません', '知識習得と自己研鑽を目的としています'].map((n) => (
                <li key={n} className="flex items-start gap-2.5 font-['Noto_Serif_JP',serif] text-sm text-[#6b6353] leading-[1.85]">
                  <i className="ri-information-line text-[#a38e65] text-base mt-0.5 shrink-0"></i>
                  <span>{n}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* ===== VII. よくある質問（アコーディオン） ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">VII. FAQ</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              よくある質問
            </h2>
            <div className="border-t border-[#e2ddd2]">
              {faqs.map((f) => (
                <details key={f.q} className="group border-b border-[#e2ddd2]">
                  <summary className="flex items-center justify-between gap-4 cursor-pointer py-5 list-none">
                    <span className="jba-display font-medium text-[#1a2942] text-base sm:text-[17px] leading-[1.6]">{f.q}</span>
                    <i className="ri-add-line text-[#a38e65] text-xl shrink-0 transition-transform group-open:rotate-45"></i>
                  </summary>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95] pb-5 pr-8">{f.a}</p>
                </details>
              ))}
            </div>
          </section>

          {/* ===== VIII. 関連資格 + 締めCTA（紺パネル）。由来: about/overview S6 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">VIII. Related Certifications</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              あわせて学びたい専門資格
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-8">
              他の専門資格も組み合わせて取得することで、より幅広い専門性を証明できます。
            </p>
            <div className="grid sm:grid-cols-3 gap-5 sm:gap-6 mb-12">
              {related.map((r) => (
                <a key={r.href} href={r.href} className="group block border border-[#e2ddd2] hover:border-[#a38e65] bg-white px-6 py-7 transition-colors">
                  <i className={`${r.icon} text-2xl text-[#a38e65]`}></i>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mt-4 mb-1.5 leading-[1.5]">{r.name}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85] mb-4">{r.ja}</p>
                  <span className="inline-flex items-center gap-2 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] group-hover:text-[#a38e65] transition-colors">
                    詳しく見る<i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                  </span>
                </a>
              ))}
            </div>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                Business Manner資格について、ご不明な点やお申込みのご相談はお気軽にお問い合わせください。専門資格の全体像は資格制度の一覧からもご覧いただけます。
              </p>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                <a href="/contact" className={BTN08_ON_NAVY}>お問い合わせ・申込</a>
                <a href="/qualifications" className={BTN08_ON_NAVY}>資格制度の一覧を見る</a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
